/**
 * SEO HEADING STRUCTURE - UniversityStudyRoomTemplate
 * ====================================================
 * 
 * TITLE: {universityName} Study Rooms | CSW | Online Study Space Alternative
 * META DESCRIPTION: Unique per page (passed via seoDescription prop)
 * 
 * H1 (1 tag):
 *   - "{universityName} Study Rooms" (hero section)
 * 
 * H2 (6 tags):
 *   - "Study Spaces at {universityShortName}" (locations table section)
 *   - "Guide & Recommendations" (guide section)
 *   - "CSW: The Online Alternative" (CSW section)
 *   - "Why Students Choose CSW" (features section)
 *   - "Summary Tips" (tips section)
 *   - "Ready to Study?" (CTA section)
 * 
 * H3 (dynamic):
 *   - Feature card titles
 *   - Study space names in table
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { 
  ArrowRight, 
  Clock, 
  Users, 
  Wifi, 
  Infinity, 
  MapPin, 
  BookOpen,
  CheckCircle2,
  XCircle,
  Building2,
  ExternalLink
} from "lucide-react";
import { Link } from "react-router-dom";

interface StudySpace {
  name: string;
  features: string;
  feedback: string;
}

interface UniversityStudyRoomTemplateProps {
  universityName: string;
  universityShortName: string;
  slug: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonicalUrl: string;
  heroDescription: string;
  studySpaces: StudySpace[];
  guideContent: React.ReactNode;
  summaryTips: string[];
  libraryWebsiteUrl?: string;
  libraryWebsiteName?: string;
}

const cswFeatures = [
  {
    icon: Infinity,
    title: "Unlimited Capacity",
    description: "No maximum occupancy. Everyone gets in, even during peak hours like finals week.",
  },
  {
    icon: Clock,
    title: "24/7 Access",
    description: "Study at any time, no closing hours or restricted access periods.",
  },
  {
    icon: Users,
    title: "Live Presence",
    description: "See other students studying and feel the motivation of shared focus.",
  },
  {
    icon: Wifi,
    title: "No Booking Required",
    description: "Click and enter immediately. No reservation systems to navigate.",
  },
];

export const UniversityStudyRoomTemplate = ({
  universityName,
  universityShortName,
  slug,
  seoTitle,
  seoDescription,
  seoKeywords,
  canonicalUrl,
  heroDescription,
  studySpaces,
  guideContent,
  summaryTips,
  libraryWebsiteUrl,
  libraryWebsiteName,
}: UniversityStudyRoomTemplateProps) => {
  // Normalize canonical URL to always be a full URL for JSON-LD
  const fullCanonicalUrl = canonicalUrl.startsWith("https://") || canonicalUrl.startsWith("http://")
    ? canonicalUrl
    : `https://csw.live${canonicalUrl}`;
  
  // Normalize to path only for SEO component
  const canonicalPath = canonicalUrl.startsWith("https://csw.live")
    ? canonicalUrl.replace("https://csw.live", "")
    : canonicalUrl;

  // Generate JSON-LD structured data
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoTitle,
    "description": seoDescription,
    "url": fullCanonicalUrl,
    "keywords": seoKeywords,
    "inLanguage": "en",
    "dateModified": new Date().toISOString().split('T')[0],
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://csw.live"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Study Room",
          "item": "https://csw.live/study-room/"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": universityName,
          "item": fullCanonicalUrl
        }
      ]
    },
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "CSW",
      "alternateName": "Chill Study Work",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web",
      "url": "https://csw.live",
      "description": `Free online study platform - alternative to ${universityName} physical study rooms. Features virtual study rooms, productivity tools, and 24/7 access.`,
      "featureList": [
        "Virtual study rooms",
        "24/7 access",
        "Unlimited capacity",
        "No booking required",
        "Pomodoro timer",
        "Task management",
        "Study with others online"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "ratingCount": "3000",
        "bestRating": "5",
        "worstRating": "1"
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW",
      "url": "https://csw.live"
    }
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `Where can I find study rooms at ${universityShortName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${universityShortName} offers study rooms in various locations including libraries and academic buildings. Check the campus library website for booking information and availability.`
        }
      },
      {
        "@type": "Question",
        "name": `Are ${universityShortName} study rooms available 24/7?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Most physical study rooms at ${universityShortName} have limited hours and may require booking. For 24/7 study access, consider online alternatives like CSW which offers unlimited virtual study rooms.`
        }
      },
      {
        "@type": "Question",
        "name": `What is an alternative to ${universityShortName} study rooms when they're full?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "CSW (Chill Study Work) offers free virtual study rooms with unlimited capacity, 24/7 access, and no booking required. It's a great alternative when physical study spaces are crowded or unavailable."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={canonicalPath}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Breadcrumb */}
            <nav className="flex justify-center items-center gap-2 text-sm text-muted-foreground animate-fade-in">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span>/</span>
              <Link to="/study-room/" className="hover:text-primary transition-colors">Study Room</Link>
              <span>/</span>
              <span className="text-foreground">{universityShortName}</span>
            </nav>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">CAMPUS STUDY GUIDE</span>
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <span className="text-gradient">{universityName}</span>{" "}
              Study Rooms
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {heroDescription}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Instead <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              {libraryWebsiteUrl && (
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                  asChild
                >
                  <a href={libraryWebsiteUrl} target="_blank" rel="noopener noreferrer">
                    {libraryWebsiteName || "Library Website"} <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Study Spaces Table Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Study Spaces at <span className="text-gradient">{universityShortName}</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                A comprehensive guide to study rooms and spaces available on campus.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 font-display font-semibold text-foreground">Location</th>
                    <th className="text-left p-4 font-display font-semibold text-foreground">Notable Features</th>
                    <th className="text-left p-4 font-display font-semibold text-foreground">User Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {studySpaces.map((space, index) => (
                    <tr 
                      key={index} 
                      className="border-b border-border/50 hover:bg-card/50 transition-colors"
                    >
                      <td className="p-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                          <h3 className="font-semibold text-foreground">{space.name}</h3>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{space.features}</td>
                      <td className="p-4 text-muted-foreground">{space.feedback}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Guide Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Guide & <span className="text-gradient">Recommendations</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="prose prose-lg prose-invert max-w-none text-muted-foreground leading-relaxed">
                {guideContent}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CSW Alternative Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                CSW: The <span className="text-gradient">Online Alternative</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                When physical study rooms are fully booked or too noisy, CSW offers a virtual solution with 10,000+ active users.
              </p>
            </div>

            {/* Comparison */}
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8 bg-card/30 backdrop-blur-sm border-border/50">
                <h3 className="font-display text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-muted-foreground" />
                  Physical Study Rooms
                </h3>
                <ul className="space-y-4">
                  {[
                    "Limited capacity, fills up fast",
                    "Requires booking in advance",
                    "Limited hours (not 24/7)",
                    "Travel time to campus",
                    "Noise from adjacent rooms",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
                <h3 className="font-display text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-primary" />
                  CSW Virtual Study Rooms
                </h3>
                <ul className="space-y-4">
                  {[
                    "Unlimited capacity, never full",
                    "No booking required, instant access",
                    "Available 24/7, 365 days",
                    "Study from anywhere",
                    "Choose your own ambiance",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-foreground">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Why Students Choose <span className="text-gradient">CSW</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cswFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={index}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 text-center"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Summary Tips */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Summary <span className="text-gradient">Tips</span>
            </h2>
            <div className="space-y-4">
              {summaryTips.map((tip, index) => (
                <Card 
                  key={index}
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{tip}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
              Ready to <span className="text-gradient">Study?</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Whether you're heading to campus or studying from home, we've got you covered. Try CSW for instant access to virtual study rooms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Enter CSW Study Room <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-border hover:bg-card font-semibold px-8 py-7 text-lg transition-all"
                asChild
              >
                <Link to="/study-room/">
                  View All Study Rooms
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Universities Navigation */}
      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-muted-foreground">Explore study rooms at other universities</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              // Toronto
              { name: "U of T St. George", slug: "university-of-toronto-st-george" },
              { name: "UTM", slug: "university-of-toronto-mississauga" },
              { name: "UTSC", slug: "university-of-toronto-scarborough" },
              { name: "York", slug: "york-university" },
              { name: "TMU", slug: "toronto-metropolitan-university" },
              { name: "OCAD", slug: "ocad-university" },
              { name: "Guelph-Humber", slug: "university-of-guelph-humber" },
              { name: "Seneca", slug: "seneca-college" },
              { name: "Humber", slug: "humber-college" },
              // Canada
              { name: "McGill", slug: "mcgill-university" },
              { name: "UBC", slug: "ubc-vancouver" },
              { name: "U of A", slug: "university-of-alberta" },
              { name: "U of C", slug: "university-of-calgary" },
              { name: "uOttawa", slug: "university-of-ottawa" },
              { name: "McMaster", slug: "mcmaster-university" },
              { name: "Waterloo", slug: "university-of-waterloo" },
              { name: "Western", slug: "western-university" },
              { name: "Queen's", slug: "queens-university" },
              { name: "U of M", slug: "university-of-manitoba" },
              { name: "USask", slug: "university-of-saskatchewan" },
              { name: "Laval", slug: "universite-laval" },
              { name: "UdeM", slug: "universite-de-montreal" },
              { name: "Dalhousie", slug: "dalhousie-university" },
              { name: "SFU", slug: "simon-fraser-university" },
              { name: "Concordia", slug: "concordia-university" },
              { name: "UVic", slug: "university-of-victoria" },
              { name: "Memorial", slug: "memorial-university" },
              { name: "Carleton", slug: "carleton-university" },
              { name: "UNB", slug: "university-of-new-brunswick" },
            ].filter(uni => uni.slug !== slug).map((uni) => (
              <Link
                key={uni.slug}
                to={`/study-room/${uni.slug}/`}
                className="px-3 py-1.5 rounded-full bg-card border border-border/50 hover:border-primary/50 hover:bg-primary/10 transition-all text-xs font-medium text-foreground"
              >
                {uni.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UniversityStudyRoomTemplate;
